import { InfiniteData, QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';

import { ResultListModel } from '@common-lib';

import { OrderModel } from '@portal-clients/order';
import {
  TransactionV2EventModel,
  TransactionV2ListModel,
  transactionV2Client,
} from '@portal-clients/transaction-v2';

import {
  useInfiniteScrollClientQuery,
  useSocketEvent,
  useSocketReconnectEvent,
} from '@frontend/hooks';

import { applyOrderEventToTransaction } from './apply-order-event-to-transaction';
import { getTransactionQueryKey } from './use-transaction-query';

const fetchTransactions = (
  accountId: string,
  pageToken: string | undefined,
) => {
  return transactionV2Client.list({
    pageSize: 20,
    pageToken,
    sortBy: {
      field: 'CREATED_AT',
      order: 'DESC',
    },
    filter: {
      accountIds: [accountId!],
      accountTypes: undefined,
      businessUnitIds: undefined,
      checkNumbers: undefined,
      checkRefs: undefined,
      from: undefined,
      ids: undefined,
      refundTransactionIds: undefined,
      siteUnitIds: undefined,
      to: undefined,
    },
  });
};

export interface UseAccountTransactionsProps {
  accountId: string | undefined;
  onFinally?(): void;
}

export const getAccountTransactionsQueryKey = (
  accountId: string | undefined,
): QueryKey => ['transactions', accountId];

export const useAccountTransactionsQuery = ({
  accountId,
  onFinally,
}: UseAccountTransactionsProps) => {
  return useInfiniteScrollClientQuery({
    enabled: !!accountId,
    queryKey: getAccountTransactionsQueryKey(accountId),
    onFetch: async (pageToken) => {
      return fetchTransactions(accountId!, pageToken);
    },
    onFinally,
  });
};

export const usePrefetchAllAccountTransactions = (accountIds: string[]) => {
  const queryClient = useQueryClient();

  const handleTransactionEvent = useCallback(
    (transaction: TransactionV2EventModel) => {
      // `type: 'all'` so a new purchase still lands in the prefetched
      // infinite cache even when the list isn't currently mounted.
      queryClient.refetchQueries({
        queryKey: getAccountTransactionsQueryKey(transaction.accountId),
        type: 'all',
      });

      // A refund (or any other financial change) is often modeled as a new
      // FSGM transaction rather than a status change on the original one,
      // so refresh whichever transaction detail page is currently open
      // regardless of which id it's keyed by.
      queryClient.refetchQueries({
        queryKey: ['transaction'],
        type: 'active',
      });

      queryClient.refetchQueries({
        queryKey: ['user'],
      });
    },
    [queryClient],
  );

  const handleOrderEvent = useCallback(
    (order: OrderModel) => {
      // Prevent an in-flight list/detail refetch (e.g. triggered by a
      // `transaction` event moments earlier) from resolving after this
      // patch and overwriting it with stale data.
      queryClient.cancelQueries({
        queryKey: getAccountTransactionsQueryKey(order.fsgmAccountId),
      });
      queryClient.cancelQueries({ queryKey: ['transaction'] });

      const patchedTransactions: TransactionV2ListModel[] = [];

      queryClient.setQueryData<
        InfiniteData<ResultListModel<TransactionV2ListModel>>
      >(getAccountTransactionsQueryKey(order.fsgmAccountId), (oldData) => {
        if (!oldData) {
          return oldData;
        }

        return {
          ...oldData,
          pages: oldData.pages.map((page) => {
            return {
              ...page,
              results: page.results.map((transaction) => {
                const patched =
                  applyOrderEventToTransaction(transaction, order) ??
                  transaction;

                if (patched !== transaction) {
                  patchedTransactions.push(patched);
                }

                return patched;
              }),
            };
          }),
        };
      });

      // Seed (or refresh) the detail cache for every row this event
      // touched, so opening a transaction for the first time after a live
      // status change never shows stale data while the GET request is in
      // flight. This component is mounted for the whole session (see
      // `Prefetch`), so it never misses an event the way a page-scoped
      // subscription would if the guest revisits the detail page after the
      // status already changed.
      for (const transaction of patchedTransactions) {
        queryClient.setQueryData(
          getTransactionQueryKey(transaction.id),
          transaction,
        );
      }

      // Also patch any already-cached detail query this event targets,
      // regardless of which FSGM transaction id (the original charge or a
      // refund created against it) the guest currently has open.
      queryClient.setQueriesData<TransactionV2ListModel>(
        { queryKey: ['transaction'] },
        (transaction) => applyOrderEventToTransaction(transaction, order),
      );
    },
    [queryClient],
  );

  useSocketEvent('transaction', handleTransactionEvent);
  useSocketEvent('order', handleOrderEvent);

  useSocketReconnectEvent(() => {
    for (const accountId of accountIds) {
      queryClient.refetchQueries({
        queryKey: getAccountTransactionsQueryKey(accountId),
        type: 'all',
      });
    }

    queryClient.refetchQueries({ queryKey: ['user'] });
  });

  useEffect(() => {
    for (const accountId of accountIds) {
      queryClient.prefetchInfiniteQuery({
        pages: 1,
        queryKey: getAccountTransactionsQueryKey(accountId),
        initialPageParam: undefined,
        getNextPageParam: (
          lastPage: ResultListModel<TransactionV2ListModel>,
        ) => {
          return lastPage.nextPageToken;
        },
        queryFn: async ({ pageParam: pageToken }) => {
          return fetchTransactions(accountId, pageToken);
        },
      });
    }
  }, [accountIds.map((id) => id).join(',')]);
};
